import { useState, useEffect } from 'react'
import { 
  createPixPayment, 
  createCardPayment,
  checkPaymentStatus,
  formatCardNumber,
  validateCardNumber,
  validateCVV,
  validateExpiration
} from '../utils/mercadoPagoService'
import { registerPayment } from '../utils/gameUnlockService'
import './PaymentModal.css'

export default function PaymentModal({ show, onClose, onPaymentComplete, gameToUnlock, userProgress, user }) {
  const [selectedAmount, setSelectedAmount] = useState(7.99)
  const [paymentMethod, setPaymentMethod] = useState('pix') // pix, card
  const [qrCodeData, setQrCodeData] = useState(null)
  const [paymentStatus, setPaymentStatus] = useState('idle') // idle, generating, waiting, confirmed, error
  const [txid, setTxid] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')
  const [isManualChecking, setIsManualChecking] = useState(false)
  
  // Card data
  const [cardData, setCardData] = useState({
    number: '',
    holderName: '',
    expirationMonth: '',
    expirationYear: '',
    cvv: '',
    cpf: ''
  })
  const [cardErrors, setCardErrors] = useState({})

  useEffect(() => {
    if (!show) {
      setQrCodeData(null)
      setPaymentStatus('idle')
      setPaymentMethod('pix')
      setTxid(null)
      setErrorMessage('')
      setIsManualChecking(false)
      setCardData({
        number: '',
        holderName: '',
        expirationMonth: '',
        expirationYear: '',
        cvv: '',
        cpf: ''
      })
      setCardErrors({})
    }
  }, [show])

  async function generatePixQRCode() {
    setPaymentStatus('generating')
    setErrorMessage('')
    
    try {
      const pixData = await createPixPayment(
        selectedAmount,
        `Desbloqueio de jogos - ${gameNames[gameToUnlock] || 'Pacote'}`,
        user.uid
      )
      
      console.log('PIX Data received:', pixData)
      
      setTxid(pixData.txid)
      setQrCodeData({
        paymentId: pixData.txid,
        amount: selectedAmount,
        qrCode: pixData.qrCode,
        qrCodeBase64: pixData.qrCodeBase64,
        pixCopyPaste: pixData.pixCopyPaste
      })
      
      setPaymentStatus('waiting')
      // Removido: startPaymentVerification(pixData.txid)
    } catch (error) {
      console.error('Error generating PIX:', error)
      const errorMsg = error.message || 'Erro ao gerar QR Code PIX. Tente novamente.'
      setErrorMessage(errorMsg)
      setPaymentStatus('error')
    }
  }

  async function processCardPayment() {
    // Valida dados do cartão
    const errors = {}
    
    if (!validateCardNumber(cardData.number)) {
      errors.number = 'Número do cartão inválido'
    }
    
    if (!cardData.holderName || cardData.holderName.length < 3) {
      errors.holderName = 'Nome do titular é obrigatório'
    }
    
    if (!validateExpiration(cardData.expirationMonth, cardData.expirationYear)) {
      errors.expiration = 'Data de validade inválida'
    }
    
    if (!validateCVV(cardData.cvv, cardData.number)) {
      errors.cvv = 'CVV inválido'
    }
    
    if (!cardData.cpf || cardData.cpf.length < 11) {
      errors.cpf = 'CPF é obrigatório'
    }
    
    if (Object.keys(errors).length > 0) {
      setCardErrors(errors)
      return
    }
    
    setPaymentStatus('generating')
    setErrorMessage('')
    setCardErrors({})
    
    try {
      const result = await createCardPayment(
        {
          number: cardData.number.replace(/\s/g, ''),
          holderName: cardData.holderName,
          expirationMonth: cardData.expirationMonth,
          expirationYear: cardData.expirationYear,
          cvv: cardData.cvv,
          identificationType: 'CPF',
          identificationNumber: cardData.cpf.replace(/\D/g, '')
        },
        selectedAmount,
        `Desbloqueio de jogos - ${gameNames[gameToUnlock] || 'Pacote'}`,
        user.uid
      )
      
      setTxid(result.txid)
      
      if (result.status === 'approved' || result.status === 'PAID') {
        setPaymentStatus('confirmed')
        await registerPayment(user.uid, {
          paymentId: result.txid,
          amount: selectedAmount,
          status: 'confirmed',
          paymentMethod: 'credit_card',
          paidAt: new Date()
        })
        
        setTimeout(() => {
          onPaymentComplete({
            paymentId: result.txid,
            amount: selectedAmount,
            status: 'confirmed'
          })
          onClose()
        }, 2000)
      } else {
        setErrorMessage('Pagamento não aprovado. Verifique os dados do cartão.')
        setPaymentStatus('error')
      }
    } catch (error) {
      console.error('Error processing card payment:', error)
      setErrorMessage(error.message || 'Erro ao processar pagamento. Tente novamente.')
      setPaymentStatus('error')
    }
  }

  function handleCardInputChange(field, value) {
    let formattedValue = value
    
    if (field === 'number') {
      formattedValue = formatCardNumber(value.replace(/\D/g, ''))
    } else if (field === 'cpf') {
      formattedValue = value.replace(/\D/g, '').substring(0, 11)
    } else if (field === 'expirationMonth' || field === 'expirationYear') {
      formattedValue = value.replace(/\D/g, '')
    } else if (field === 'cvv') {
      formattedValue = value.replace(/\D/g, '').substring(0, 4)
    }
    
    setCardData(prev => ({
      ...prev,
      [field]: formattedValue
    }))
    
    // Limpa erro ao digitar
    if (cardErrors[field]) {
      setCardErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  function copyPixCode() {
    if (qrCodeData) {
      navigator.clipboard.writeText(qrCodeData.pixCopyPaste)
      alert('✅ Código PIX copiado! Cole no seu app de banco.')
    }
  }

  async function manualCheckPayment() {
    if (!txid) return
    
    setIsManualChecking(true)
    setErrorMessage('')
    
    try {
      const result = await checkPaymentStatus(txid)
      
      if (result.paid) {
        setPaymentStatus('confirmed')
        
        await registerPayment(user.uid, {
          paymentId: txid,
          amount: selectedAmount,
          status: 'confirmed',
          paymentMethod: 'pix',
          paidAt: result.paidAt || new Date()
        })
        
        setTimeout(() => {
          onPaymentComplete({
            paymentId: txid,
            amount: selectedAmount,
            status: 'confirmed'
          })
          onClose()
        }, 2000)
      } else {
        setErrorMessage('Pagamento ainda não confirmado. Por favor, aguarde.')
      }
    } catch (error) {
      console.error('Error checking payment manually:', error)
      setErrorMessage('Erro ao verificar pagamento. Tente novamente.')
    } finally {
      setIsManualChecking(false)
    }
  }

  if (!show) return null

  const gameNames = {
    snake: 'Snake',
    pong: 'Pong',
    breakout: 'Breakout',
    sancode: 'San Code'
  }

  const unlockedCount = userProgress?.unlockedGames?.length || 1

  return (
    <div className="payment-modal-overlay" onClick={onClose}>
      <div className="payment-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>
        
        <h2>🔓 Desbloquear Jogos</h2>
        
        {paymentStatus === 'idle' && (
          <>
            <div className="unlock-info">
              <p><strong>📦 Preview Grátis:</strong> Todos os jogos têm 3 fases de teste!</p>
              <p><strong>🆓 Tetris:</strong> Liberado completo e gratuito!</p>
              <p className="unlock-methods">Você pode desbloquear jogos:</p>
              <ul>
                <li>⏱️ Jogando 2 horas no preview</li>
                <li>💰 Comprando via PIX ou Cartão</li>
              </ul>
            </div>

            <div className="payment-options">
              <div 
                className={`payment-option ${selectedAmount === 7.99 ? 'selected' : ''}`}
                onClick={() => setSelectedAmount(7.99)}
              >
                <div className="option-header">
                  <span className="option-price">R$ 7,99</span>
                  <span className="option-badge">Individual</span>
                </div>
                <p>Desbloqueia: <strong>{gameNames[gameToUnlock]}</strong></p>
                <small>Apenas este jogo completo</small>
              </div>

              <div 
                className={`payment-option ${selectedAmount === 40 ? 'selected' : ''}`}
                onClick={() => setSelectedAmount(40)}
              >
                <div className="option-header">
                  <span className="option-price">R$ 40,00</span>
                  <span className="option-badge recommended">Melhor Oferta</span>
                </div>
                <p>Desbloqueia: <strong>TODOS os Jogos!</strong></p>
                <small>✨ Todos os jogos completos (Snake, Pong, Breakout, San Code)</small>
              </div>
            </div>

            <div className="payment-method-selector">
              <button 
                className={`method-btn ${paymentMethod === 'pix' ? 'active' : ''}`}
                onClick={() => setPaymentMethod('pix')}
              >
                📱 PIX
              </button>
              <button 
                className={`method-btn ${paymentMethod === 'card' ? 'active' : ''}`}
                onClick={() => setPaymentMethod('card')}
              >
                💳 Cartão
              </button>
            </div>

            {paymentMethod === 'pix' && (
              <button className="btn-primary" onClick={generatePixQRCode}>
                Gerar QR Code PIX
              </button>
            )}

            {paymentMethod === 'card' && (
              <div className="card-form">
                <div className="form-group">
                  <label>Número do Cartão</label>
                  <input
                    type="text"
                    placeholder="0000 0000 0000 0000"
                    value={cardData.number}
                    onChange={(e) => handleCardInputChange('number', e.target.value)}
                    maxLength="19"
                    className={cardErrors.number ? 'error' : ''}
                  />
                  {cardErrors.number && <span className="error-text">{cardErrors.number}</span>}
                </div>

                <div className="form-group">
                  <label>Nome do Titular</label>
                  <input
                    type="text"
                    placeholder="Nome como está no cartão"
                    value={cardData.holderName}
                    onChange={(e) => handleCardInputChange('holderName', e.target.value.toUpperCase())}
                    className={cardErrors.holderName ? 'error' : ''}
                  />
                  {cardErrors.holderName && <span className="error-text">{cardErrors.holderName}</span>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Validade</label>
                    <div className="expiration-group">
                      <input
                        type="text"
                        placeholder="MM"
                        value={cardData.expirationMonth}
                        onChange={(e) => handleCardInputChange('expirationMonth', e.target.value)}
                        maxLength="2"
                        className={cardErrors.expiration ? 'error' : ''}
                      />
                      <span>/</span>
                      <input
                        type="text"
                        placeholder="AAAA"
                        value={cardData.expirationYear}
                        onChange={(e) => handleCardInputChange('expirationYear', e.target.value)}
                        maxLength="4"
                        className={cardErrors.expiration ? 'error' : ''}
                      />
                    </div>
                    {cardErrors.expiration && <span className="error-text">{cardErrors.expiration}</span>}
                  </div>

                  <div className="form-group">
                    <label>CVV</label>
                    <input
                      type="text"
                      placeholder="123"
                      value={cardData.cvv}
                      onChange={(e) => handleCardInputChange('cvv', e.target.value)}
                      maxLength="4"
                      className={cardErrors.cvv ? 'error' : ''}
                    />
                    {cardErrors.cvv && <span className="error-text">{cardErrors.cvv}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label>CPF do Titular</label>
                  <input
                    type="text"
                    placeholder="000.000.000-00"
                    value={cardData.cpf}
                    onChange={(e) => handleCardInputChange('cpf', e.target.value)}
                    maxLength="11"
                    className={cardErrors.cpf ? 'error' : ''}
                  />
                  {cardErrors.cpf && <span className="error-text">{cardErrors.cpf}</span>}
                </div>

                <button className="btn-primary" onClick={processCardPayment}>
                  Pagar com Cartão
                </button>
              </div>
            )}
          </>
        )}

        {paymentStatus === 'generating' && (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Gerando QR Code PIX...</p>
          </div>
        )}

        {paymentStatus === 'waiting' && qrCodeData && (
          <div className="qr-code-display">
            <h3>Escaneie o QR Code</h3>
            {qrCodeData.qrCodeBase64 ? (
              <img 
                src={qrCodeData.qrCodeBase64} 
                alt="QR Code PIX" 
                className="qr-code-image"
                onError={(e) => {
                  console.error('Error loading QR Code image')
                  e.target.style.display = 'none'
                }}
              />
            ) : (
              <div className="qr-code-error">
                <p>⚠️ Erro ao carregar QR Code</p>
                <small>Use o código PIX abaixo</small>
              </div>
            )}
            
            <div className="payment-details">
              <p><strong>Valor:</strong> R$ {selectedAmount},00</p>
            </div>

            <div className="pix-copy-paste">
              <p><strong>Ou copie o código PIX:</strong></p>
              <div className="code-box">
                <code>{qrCodeData.pixCopyPaste.substring(0, 50)}...</code>
              </div>
              <button className="btn-secondary" onClick={copyPixCode}>
                📋 Copiar Código PIX
              </button>
            </div>

            <div className="payment-waiting">
              <div className="spinner"></div>
              <p>Aguardando pagamento...</p>
              <small>O pagamento será confirmado automaticamente</small>
              
              <button 
                className="btn-secondary verify-payment-btn" 
                onClick={manualCheckPayment}
                disabled={isManualChecking}
              >
                {isManualChecking ? '🔄 Verificando...' : '🔍 Verificar Pagamento Agora'}
              </button>
              
              {errorMessage && (
                <div className="verification-message">
                  <small>{errorMessage}</small>
                </div>
              )}
            </div>
          </div>
        )}

        {paymentStatus === 'confirmed' && (
          <div className="payment-confirmed">
            <div className="success-icon">✅</div>
            <h3>Pagamento Confirmado!</h3>
            <p>Desbloqueando jogos...</p>
          </div>
        )}

        {paymentStatus === 'error' && (
          <div className="payment-error">
            <div className="error-icon">❌</div>
            <h3>Erro no Pagamento</h3>
            <p>{errorMessage}</p>
            <button className="btn-secondary" onClick={() => setPaymentStatus('idle')}>
              Tentar Novamente
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
